// Client-side estimate of the server clock. Never compare Date.now() across devices: every
// time the client reasons about is server time, estimated from ping/pong round trips.
//
// The estimate follows the sample with the smallest round trip, because the smaller the RTT the
// tighter the bound on one-way delay. That minimum leaks upward slowly so a stale best sample
// (taken before the network got worse, or before the main thread started stalling) cannot pin
// the offset forever.

export class ServerClock {
  private offset = 0;
  private bestRtt = Infinity;
  private bestAt = 0;
  private synced = false;
  /** How fast the best RTT forgets, in ms of RTT per ms of wall time. */
  private readonly leak: number;

  constructor(leakPerSecond = 2) {
    this.leak = leakPerSecond / 1000;
  }

  /** Seed from the welcome message before any round trip has completed. */
  seed(serverTime: number, localNow: number): void {
    if (!this.synced) this.offset = serverTime - localNow;
  }

  /**
   * Feed one round trip.
   * @param c  local time the ping was sent
   * @param s  server time when the pong was produced
   * @param now local time the pong arrived
   */
  sample(c: number, s: number, now: number): void {
    const rtt = now - c;
    if (!(rtt >= 0)) return;
    const threshold = this.synced ? this.bestRtt + (now - this.bestAt) * this.leak : Infinity;
    if (rtt > threshold) return;
    this.bestRtt = rtt;
    this.bestAt = now;
    const estimate = s + rtt / 2 - now;
    this.offset = this.synced ? this.offset + (estimate - this.offset) * 0.5 : estimate;
    this.synced = true;
  }

  /** Server time corresponding to a local timestamp. */
  now(localNow: number): number {
    return localNow + this.offset;
  }

  get ready(): boolean {
    return this.synced;
  }

  /** Best recent round-trip time in ms, or Infinity before the first pong. */
  get rtt(): number {
    return this.bestRtt;
  }
}
